/**
 * Auth telemetry slice — surfaces token-lifecycle events to the developer header.
 *
 * Owned by the shared atoll module so every Atoll-compliant frontend gets the
 * same chips automatically. Consumers must register `authDebug` on their store
 * (see frontend/src/services/redux/store.ts).
 *
 * Source events:
 * - atollAuth.ts dispatches refreshStarted / refreshSucceeded / refreshFailed,
 *   and increments / decrements refreshQueueDepth around the single-flight
 *   refreshingPromise so concurrent fetch waiters are visible.
 * - atollFetch.ts dispatches authRejected on every 401/403 received.
 *
 * Timestamps are epoch ms; the developer header treats them as "fire and fade"
 * — a useEffect on the timestamp value sets a local visible flag and clears
 * it after the chip's display window.
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface AuthDebugState {
  lastRefreshAt: number;
  lastRefreshFailedAt: number;
  refreshQueueDepth: number;
  lastRejectionAt: number;
  lastJwtError: string | null;
  loginRequired: boolean;
}

const initialState: AuthDebugState = {
  lastRefreshAt: 0,
  lastRefreshFailedAt: 0,
  refreshQueueDepth: 0,
  lastRejectionAt: 0,
  lastJwtError: null,
  loginRequired: false,
};

export const authDebugSlice = createSlice({
  name: "authDebug",
  initialState,
  reducers: {
    refreshStarted: (state) => {
      state.lastRefreshAt = Date.now();
    },
    refreshSucceeded: (state) => {
      state.lastJwtError = null;
      state.loginRequired = false;
    },
    refreshFailed: (state, action: PayloadAction<string | undefined>) => {
      state.lastRefreshFailedAt = Date.now();
      state.lastJwtError = action.payload || "refresh failed";
    },
    refreshQueueIncrement: (state) => {
      state.refreshQueueDepth += 1;
    },
    refreshQueueDecrement: (state) => {
      state.refreshQueueDepth = Math.max(0, state.refreshQueueDepth - 1);
    },
    authRejected: (state) => {
      state.lastRejectionAt = Date.now();
    },
    setLoginRequired: (state, action: PayloadAction<boolean>) => {
      state.loginRequired = action.payload;
    },
    clearAuthDebug: () => initialState,
  },
});

export const {
  refreshStarted,
  refreshSucceeded,
  refreshFailed,
  refreshQueueIncrement,
  refreshQueueDecrement,
  authRejected,
  setLoginRequired,
  clearAuthDebug,
} = authDebugSlice.actions;

export const selectLastRefreshAt = (s: { authDebug: AuthDebugState }) => s.authDebug.lastRefreshAt;
export const selectLastRefreshFailedAt = (s: { authDebug: AuthDebugState }) => s.authDebug.lastRefreshFailedAt;
export const selectRefreshQueueDepth = (s: { authDebug: AuthDebugState }) => s.authDebug.refreshQueueDepth;
export const selectLastRejectionAt = (s: { authDebug: AuthDebugState }) => s.authDebug.lastRejectionAt;
export const selectLastJwtError = (s: { authDebug: AuthDebugState }) => s.authDebug.lastJwtError;
export const selectLoginRequired = (s: { authDebug: AuthDebugState }) => s.authDebug.loginRequired;

export default authDebugSlice.reducer;
